/**
 * FLAT pricing (docs/PRICING-MODEL.md, migration 2026-08-20) — the client half.
 *
 * SOURCE OF TRUTH IS THE SERVER (CLAUDE.md invariant 4, the rule that killed
 * the hardcoded concierge-fee mirror): `/api/health` serves a `flatPricing`
 * block ({ live, serviceFeeUsd, serviceFeeMemberUsd, freeSourcesIncluded,
 * guarantee…, cardSurchargePct }) and this module is a LIVE BINDING seeded
 * with the launch defaults and re-pointed by `hydrateFlatPricing()`, which the
 * existing boot fetch in `askaServices.ts` calls. Nothing here is ever
 * charged — the server quotes and snapshots every order.
 *
 * ── `live` IS THE MODEL SWITCH ────────────────────────────────────────────
 * `live: true` (the seed since 2026-09-11 — FLAT_PRICING_LIVE has been ARMED
 * in production since 2026-08-21) ⇒ browse shows the REAL supplier price
 * (multiplier 1 — transparency is the pitch) and checkout renders the flat
 * lines. `live: false` ⇒ the legacy percentage model (browse multiplies by
 * 1 + fee%), kept only so an explicit server rollback still re-points the
 * client. A missing/malformed payload leaves the CURRENT model standing —
 * the safe direction is "what production is armed to", never a half-
 * hydrated flip. (The seed used to be false, which made every first paint
 * flash 15%-inflated prices until /health answered.)
 */

export interface FlatPricing {
  live: boolean
  serviceFeeUsd: number
  serviceFeeMemberUsd: number
  freeSourcesIncluded: number
  guaranteeKind: 'percent' | 'flat'
  guaranteeValue: number
  guaranteeMemberValue: number
  cardSurchargePct: number
}

/** Display seed only — the server is authoritative and re-points this at boot. */
export let FLAT_PRICING: FlatPricing = {
  live: true,
  serviceFeeUsd: 15,
  serviceFeeMemberUsd: 0,
  freeSourcesIncluded: 2,
  guaranteeKind: 'percent',
  guaranteeValue: 3.5,
  guaranteeMemberValue: 2,
  cardSurchargePct: 3,
}

/**
 * Re-point the model from `/api/health`, then broadcast BOTH
 * `aska:flat-pricing-changed` (for copy branches) and `aska:fee-changed` (so
 * every derived price multiplier — mobile ASKA_MULT and friends — recomputes
 * through the same event it already listens to). Validated, not trusted.
 */
export function hydrateFlatPricing(raw: unknown): void {
  if (!raw || typeof raw !== 'object') return
  const p = raw as Partial<Record<keyof FlatPricing, unknown>>
  const num = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback

  const next: FlatPricing = {
    // Only an EXPLICIT boolean moves the switch — a missing/junk `live` keeps
    // the current model (never flip the armed model off a malformed payload;
    // an explicit `live:false` from the server still rolls the client back).
    live: typeof p.live === 'boolean' ? p.live : FLAT_PRICING.live,
    serviceFeeUsd: num(p.serviceFeeUsd, FLAT_PRICING.serviceFeeUsd),
    serviceFeeMemberUsd: num(p.serviceFeeMemberUsd, FLAT_PRICING.serviceFeeMemberUsd),
    freeSourcesIncluded: num(p.freeSourcesIncluded, FLAT_PRICING.freeSourcesIncluded),
    guaranteeKind: p.guaranteeKind === 'flat' ? 'flat' : 'percent',
    guaranteeValue: num(p.guaranteeValue, FLAT_PRICING.guaranteeValue),
    guaranteeMemberValue: num(p.guaranteeMemberValue, FLAT_PRICING.guaranteeMemberValue),
    cardSurchargePct: num(p.cardSurchargePct, FLAT_PRICING.cardSurchargePct),
  }
  if (JSON.stringify(next) === JSON.stringify(FLAT_PRICING)) return
  FLAT_PRICING = next
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('aska:flat-pricing-changed'))
    // Multiplier consumers already subscribe to this one — reuse it so the
    // browse price flips to the real price the moment the flat model hydrates.
    window.dispatchEvent(new Event('aska:fee-changed'))
  }
}

/** "$15" / "$12.50" — the flat fee lines are small round numbers; keep cents
 *  only when they exist. */
export function formatFlatUsd(n: number): string {
  const v = Number.isFinite(n) ? n : 0
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`
}

/** The guarantee rate as copy — "3.5%" (percent kind) or "$40" (flat kind). */
export function formatGuaranteeRate(member: boolean): string {
  const rate = member ? FLAT_PRICING.guaranteeMemberValue : FLAT_PRICING.guaranteeValue
  return FLAT_PRICING.guaranteeKind === 'percent' ? `${rate}%` : formatFlatUsd(rate)
}
